import { message } from "antd";
import { PlateFormatType } from "store/lobby/lobby-model";
import { IProcessData } from "store/location/location-model";
import { validationCharacters } from "utils/functions";
import useTranslation from "utils/hooks/useTranslation";

export type IValidateCreateUnit = {
  checkinActive: boolean;
  hasAttendanceActive: boolean;
  processList: Array<IProcessData>;
  originalProcessList: Array<IProcessData>;
  dropHookTypeActive: number;
  PROCESS_REQUIRED_MESSAGE: string;
  MESSAGE_ACTIVATE_CHECKIN: string;
  MANEUVER_PROCESS_REQUIRED_MESSAGE: string;
};

export const hasErrors = (fieldsError) => Object.keys(fieldsError).some((field) => fieldsError[field]);

export const validationPlate = /^[A-Za-z]{3}\d[0-9A-Za-z]\d{2}$/;
export const validationSpecialCharacters = /^[a-zA-Z0-9]*$/;
export const validationNumber = /^\d*$/;
export const validationCompanyDocument = /^[a-zA-Z0-9./-]+$/;
export const validationLetter = /^[a-zA-Z_ \u00C0-\u024F\u1E00-\u1EFF]*$/;
export const validationCPF = /^\d{3}[.]{1}\d{3}[.]{1}\d{3}-{1}\d{2}$/;
export const validationLetterAndNumber = /^[a-zA-Z0-9_ \u00C0-\u024F\u1E00-\u1EFF]*$/;
export const validationDriverLicense = /^[A-Za-z0-9]{7,13}$/;
export const validationLetterAndSpecialCharacters = /^[a-zA-ZÀ-ú$&+,:;/_{}=?@#|'<>.^*()%!-]*$/;
export const validationOnlySpecialCharacters = /[$&+,:;/_{}=?@#|'<>.^*()%!-]/;
export const validatioNumberAndSpecialCharacters = /[0-9$&+,:;/_{}=?@#|'<>.^*()%!-]/;
export const validationAllowAll = /[^\n]+/;
export const validationNotAllow = /[.]/;
export const requiredDocumentOptions = [
  { id: 0, description: "Opcional" },
  { id: 1, description: "Obrigatório na entrada" },
  { id: 2, description: "Obrigatório na saída" },
  { id: 3, description: "Obrigatório na entrada e saída" },
];

export const maxLengthPlateFormatType = (plateFormatType: PlateFormatType | null | undefined): number => (plateFormatType === 1 ? 10 : plateFormatType === 2 ? 9 : 7);

export const checkPlateFormatType = (type: PlateFormatType | null | undefined): RegExp => {
  switch (type) {
    // 0 = Brazil/Mercosur (ABC1234 ou ABC1D23)
    case PlateFormatType.BRAZIL_MERCOSUR:
      return /^(?:[A-Z]{3}[0-9]{4}|[A-Z]{3}[0-9][A-Z][0-9]{2})$/i;

    // 1 = Custom (6-10 alfanumeric)
    case PlateFormatType.CUSTOM:
      return /^[A-Z0-9]{6,10}$/i;

    // 2 = Mexico (1-9 alfanumeric)
    case PlateFormatType.MEXICO:
      return /^[A-Z0-9]{1,9}$/i;

    //Fallback Brazil/Mercosur
    default:
      return /^(?:[A-Z]{3}[0-9]{4}|[A-Z]{3}[0-9][A-Z][0-9]{2})$/i;
  }
};

export const validateCreateUnit = ({
  originalProcessList,
  processList,
  checkinActive,
  dropHookTypeActive,
  hasAttendanceActive,
  PROCESS_REQUIRED_MESSAGE,
  MESSAGE_ACTIVATE_CHECKIN,
  MANEUVER_PROCESS_REQUIRED_MESSAGE,
}: IValidateCreateUnit): boolean => {
  let maneuverIsActive;
  const hasManeuver = originalProcessList.find((item) => item.description === "Manobra");
  if (hasManeuver) {
    maneuverIsActive = processList.some((item) => item.id === hasManeuver.id && item.active);
  } else {
    maneuverIsActive = false;
  }

  const conditions = [
    { condition: processList.every((x) => x.active === false), messages: PROCESS_REQUIRED_MESSAGE },
    {
      condition: hasManeuver && !maneuverIsActive && [1, 2, 3].includes(dropHookTypeActive),
      messages: MANEUVER_PROCESS_REQUIRED_MESSAGE,
    },
    { condition: hasAttendanceActive && !checkinActive, messages: MESSAGE_ACTIVATE_CHECKIN },
  ];

  const isValid = conditions.every(({ condition, messages }) => {
    if (condition) {
      message.error(messages);
      return false;
    }
    return true;
  });

  return isValid;
};

export const maxCharactersDocuments = (documentInputDT: boolean, documentInputPedido: boolean) => {
  const maxCharactersDocumentDT = 8;
  const maxCharactersDocumentPedido = 10;
  if (documentInputDT) {
    return maxCharactersDocumentDT;
  }
  if (documentInputPedido) {
    return maxCharactersDocumentPedido;
  }
  return null;
};

export const validation = (e) => {
  validationCharacters(e, validationNumber);
};

export const validationDocuments = (e, validation) => {
  validationCharacters(e, validation);
};

const ALLOW_LETTER = 1;
const ALLOW_NUMBER = 2;
const ALLOW_SPECIAL_CHARACTERS = 4;

export const validationMap = {
  [ALLOW_LETTER]: validationLetter,
  [ALLOW_NUMBER]: validationNumber,
  [ALLOW_SPECIAL_CHARACTERS]: validationOnlySpecialCharacters,
  [ALLOW_LETTER | ALLOW_NUMBER]: validationLetterAndNumber,
  [ALLOW_LETTER | ALLOW_SPECIAL_CHARACTERS]: validationLetterAndSpecialCharacters,
  [ALLOW_NUMBER | ALLOW_SPECIAL_CHARACTERS]: validatioNumberAndSpecialCharacters,
  [ALLOW_LETTER | ALLOW_NUMBER | ALLOW_SPECIAL_CHARACTERS]: validationAllowAll,
};

export const charactersDocumentsValidation = (e, allowLetter, allowNumber, allowSpecialCharacters) => {
  const allowedCharacters = (allowLetter ? ALLOW_LETTER : 0) | (allowNumber ? ALLOW_NUMBER : 0) | (allowSpecialCharacters ? ALLOW_SPECIAL_CHARACTERS : 0);
  const validationRegex = validationMap[allowedCharacters] || validationNotAllow;
  return validationDocuments(e, validationRegex);
};

export const rulesPlates = () => {
  return [
    {
      pattern: validationPlate,
      message: useTranslation({ id: "invalid_plate" }),
    },
  ];
};

export const checkIfIsEqualInboundProcess = (outboundProcess, registerActive) => (outboundProcess.includes(registerActive.processId) ? registerActive.processDescription : null);

export const documentInboundRules = (value, eventInboundActive, eventOutboundActive, invalidEvent) => {
  return (value && eventInboundActive && eventOutboundActive) ||
    (value && eventInboundActive && !eventOutboundActive) ||
    (!value && eventInboundActive && eventOutboundActive) ||
    (!value && !eventInboundActive && !eventOutboundActive) ||
    (!value && !eventInboundActive && eventOutboundActive)
    ? Promise.resolve()
    : Promise.reject(new Error(invalidEvent));
};

export const documentOutboundRules = (value, eventInboundActive, eventOutboundActive, invalidEvent) => {
  return (value && eventInboundActive && eventOutboundActive) ||
    (value && eventOutboundActive && !eventInboundActive) ||
    (!value && eventInboundActive && eventOutboundActive) ||
    (!value && !eventInboundActive && !eventOutboundActive) ||
    (!value && eventInboundActive && !eventOutboundActive)
    ? Promise.resolve()
    : Promise.reject(new Error(invalidEvent));
};

export const checkIfIsRequiredDocument = (documents) => {
  let isRequiredOutboundDocument;
  let isRequiredInboundDocument;
  const requiredInbound = 1;
  const requiredOutbound = 2;
  const requiredInboundAndOutbound = 3;
  documents?.map((x) => {
    const checkOutbound = x.documentOutbound === true && x.documentInbound === false && (x.required === requiredInbound || x.required === requiredInboundAndOutbound);
    if (checkOutbound) isRequiredOutboundDocument = true;
    const checkInbound = x.documentOutbound === false && x.documentInbound === true && (x.required === requiredOutbound || x.required === requiredInboundAndOutbound);
    if (checkInbound) isRequiredInboundDocument = true;
  });

  return { isRequiredInboundDocument, isRequiredOutboundDocument };
};

export const checkIfIsFeaturedOrInLocationToShowViewDrawer = (
  eventType,
  yardRegisterId,
  lobbyFetchItemCheckinAsync,
  vehicleDashboardShowDrawerViewCheckinRegister,
  lobbyFetchItemAsync,
  vehicleDashboardShowDrawerViewRegister
) => {
  const isCheckinData = 0;
  if (eventType === isCheckinData) {
    lobbyFetchItemCheckinAsync(yardRegisterId);
    vehicleDashboardShowDrawerViewCheckinRegister();
  } else {
    lobbyFetchItemAsync(yardRegisterId);
    vehicleDashboardShowDrawerViewRegister();
  }
};

export const driverValidator = (form, driverFieldName, cpfFieldName, cpfReset, REQUIRED_FIELD, required = false) => ({
  required,
  validator(_, value) {
    const driverValue = form.getFieldValue(driverFieldName);
    const cpfValue = form.getFieldValue(cpfFieldName);
    const hasDriver = !!driverValue;
    const hasDocument = !!cpfValue && cpfValue !== cpfReset;
    const isEmptyValue = !value || value === cpfReset;

    if ((required || hasDriver || hasDocument) && isEmptyValue) {
      return Promise.reject(REQUIRED_FIELD);
    }

    return Promise.resolve();
  },
});
